import { ServiceRouter, DEFAULT_SERVICE, resolvePublicService, type PublicServiceId } from './ServiceRouter.js';
import { listDomains, PLATFORM } from '../lib/DomainRegistry.js';

export type { PublicServiceId } from './ServiceRouter.js';

export interface NavigationService {
    id: PublicServiceId;
    title: string;
    description: string;
    icon: string;
    swatch?: string;
}

/**
 * The command bar: brand, one compact tab per public service (Systems Lab
 * first, then each enabled domain), and the dock/admin actions. Tab list
 * comes from the registry; the Workspace owns the docks this bar toggles.
 */
function buildServices(): NavigationService[] {
    const overview: NavigationService = {
        id: 'overview', title: PLATFORM.name, description: PLATFORM.headline, icon: 'fa-flask',
    };
    const start: NavigationService = {
        id: 'start', title: 'Start', description: 'Start a project, upload a scheme, or open one you hold', icon: 'fa-circle-play',
    };
    const setup: NavigationService = {
        id: 'setup', title: 'Project setup', description: 'Bind a network facility, author the parcel and scope, see the permit map', icon: 'fa-list-check',
    };
    const domains: NavigationService[] = listDomains().map(d => ({
        id: d.id, title: d.navTitle, description: d.tagline, icon: d.icon, swatch: d.accent,
    }));
    return [start, overview, setup, ...domains];
}

export class NavigationHeader {
    private services: NavigationService[] = buildServices();
    private currentService: PublicServiceId = DEFAULT_SERVICE;
    private serviceRouter: ServiceRouter | null = null;
    private isInAdminMode = false;
    private isAuthenticated = false;
    private preAdminService: PublicServiceId = DEFAULT_SERVICE;
    private host: HTMLElement | null = null;

    public setServiceRouter(router: ServiceRouter): void {
        this.serviceRouter = router;
    }

    /** Render the command bar into the given host (the Workspace top bar). */
    public renderInto(host: HTMLElement): void {
        this.host = host;
        host.innerHTML = `
            <div class="ws-brand"><img src="/mokulab.svg" alt="MokuLab.io"></div>
            <div class="ws-tabs">${this.services.map(s => this.renderTab(s)).join('')}</div>
            <div class="ws-actions">
                <button class="ws-btn" id="ws-welcome-btn" title="Welcome: MokuLab in the Mokunet network"><i class="fa-solid fa-map-location-dot"></i><span>Welcome</span></button>
                <button class="ws-btn" id="ws-toggle-left" title="Navigator (Ctrl+B)"><i class="fa-solid fa-table-columns"></i></button>
                <button class="ws-btn" id="info-modal-btn" title="Info (F1)"><i class="fa-solid fa-circle-info"></i><span>Info</span></button>
                <button class="ws-btn" id="admin-login-btn" title="Admin" style="display:none;"><i class="fa-solid fa-lock"></i><span class="tab-admin-text">Admin</span></button>
            </div>`;
        this.attachEventListeners(host);
        this.refreshAdminButton();
    }

    private renderTab(s: NavigationService): string {
        const active = s.id === this.currentService && !this.isInAdminMode;
        const marker = s.swatch ? `<span class="ws-swatch" style="--swatch:${s.swatch}"></span>` : `<i class="fa-solid ${s.icon}" style="font-size:11px"></i>`;
        return `<div class="ws-tab ${active ? 'active' : ''}" data-service="${s.id}" title="${s.description}">${marker}<span>${s.title}</span></div>`;
    }

    /** Switch to a public service. Accepts the legacy 'digital-twin-workflow' id. Exits admin first. */
    public switchToService(serviceId: PublicServiceId | string): void {
        const resolved = resolvePublicService(serviceId) ?? DEFAULT_SERVICE;
        if (this.isInAdminMode) {
            this.isInAdminMode = false;
            this.refreshAdminButton();
        }
        this.currentService = resolved;
        this.updateTabActiveStates();
        this.serviceRouter?.switchService(resolved);
    }

    public async enterAdminMode(tab?: string): Promise<void> {
        if (this.isInAdminMode) { if (tab && this.serviceRouter) await this.serviceRouter.switchToProtectedService('admin', tab); return; }
        this.preAdminService = this.currentService;
        this.isInAdminMode = true;
        this.updateTabActiveStates();
        this.refreshAdminButton();
        if (this.serviceRouter) await this.serviceRouter.switchToProtectedService('admin', tab);
    }

    public exitAdminMode(): void {
        if (!this.isInAdminMode) return;
        this.switchToService(this.preAdminService);
    }

    private updateTabActiveStates(): void {
        this.host?.querySelectorAll('.ws-tab').forEach(tab => {
            tab.classList.toggle('active', !this.isInAdminMode && tab.getAttribute('data-service') === this.currentService);
        });
    }

    private attachEventListeners(host: HTMLElement): void {
        host.addEventListener('click', (event) => {
            const tab = (event.target as HTMLElement).closest<HTMLElement>('.ws-tab');
            if (tab) {
                const id = tab.getAttribute('data-service');
                if (id && (this.isInAdminMode || id !== this.currentService)) this.switchToService(id);
            }
        });
        host.querySelector('#admin-login-btn')?.addEventListener('click', () => {
            if (this.isInAdminMode) this.exitAdminMode(); else void this.enterAdminMode();
        });
    }

    public updateAdminButton(isAuthenticated: boolean): void {
        this.isAuthenticated = isAuthenticated;
        this.refreshAdminButton();
    }

    private refreshAdminButton(): void {
        const btn = this.host?.querySelector<HTMLElement>('#admin-login-btn');
        if (!btn) return;
        if (import.meta.env.VITE_ENABLE_ADMIN !== 'true') { btn.style.display = 'none'; return; }
        btn.style.display = 'inline-flex';
        const text = btn.querySelector('.tab-admin-text');
        const icon = btn.querySelector('i');
        if (this.isInAdminMode) {
            if (text) text.textContent = 'Back';
            if (icon) icon.className = 'fa-solid fa-arrow-left';
            btn.title = 'Back to the lab';
        } else if (this.isAuthenticated) {
            if (text) text.textContent = 'Admin';
            if (icon) icon.className = 'fa-solid fa-gear';
            btn.title = 'Admin panel';
        } else {
            if (text) text.textContent = 'Admin';
            if (icon) icon.className = 'fa-solid fa-lock';
            btn.title = 'Admin login';
        }
    }

    public getIsInAdminMode(): boolean { return this.isInAdminMode; }
    public setAdminMode(active: boolean): void { this.isInAdminMode = active; this.updateTabActiveStates(); this.refreshAdminButton(); }
    public getCurrentService(): PublicServiceId { return this.currentService; }
    public getService(serviceId: PublicServiceId): NavigationService | undefined { return this.services.find(s => s.id === serviceId); }
}
